import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const ShareContainer = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 0.75rem;
    right: 0.75rem;
  }

  @media (max-width: 480px) {
    bottom: 0.5rem;
    right: 0.5rem;
  }
`;

const ShareBtn = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border: 1px solid #C4B5A0;
  padding: 0.6rem 0.9rem;
  border-radius: 20px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: rgba(232, 220, 200, 0.95);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.7rem;
    font-size: 0.85rem;
    border-radius: 16px;
  }
`;

const ShareMenu = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 0.5rem;
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  min-width: 180px;
  overflow: hidden;
  animation: slideUp 0.2s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 480px) {
    min-width: 160px;
  }
`;

const ShareOption = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  padding: 0.75rem 1rem;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid #E8DCC8;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #F0E8D8;
  }

  @media (max-width: 480px) {
    padding: 0.65rem 0.85rem;
    font-size: 0.8rem;
  }
`;

function ShareButton({ filePath, people, summary }) {
  const [copied, setCopied] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createSlug = (path) => {
    // Remove extension and replace slashes with hyphens
    const withoutExt = path.substring(0, path.lastIndexOf('.')) || path;
    const slug = withoutExt.replace(/\//g, '-');
    return slug;
  };

  const generateTweetText = () => {
    // Create context-aware message based on document content
    const baseText = "Check out this document from the Epstein Archive";
    
    if (people) {
      // Extract first 1-2 names mentioned
      const peopleList = people.split(',').map(p => p.trim());
      const firstPerson = peopleList[0];
      
      if (firstPerson && firstPerson.length < 50) {
        return `Document about ${firstPerson} from the Epstein Archive 🔍`;
      }
    }
    
    return `${baseText} 🔍`;
  };

  const handleCopyLink = () => {
    const slug = createSlug(filePath);
    const url = `${window.location.origin}/document/${slug}`;
    
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setShowMenu(false);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleShareOnX = () => {
    const slug = createSlug(filePath);
    const url = `${window.location.origin}/document/${slug}`;
    const tweetText = generateTweetText();
    
    // X (Twitter) intent URL
    const xIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`;
    
    window.open(xIntentUrl, '_blank', 'width=550,height=420');
    setShowMenu(false);
  };

  return (
    <ShareContainer ref={menuRef}>
      {showMenu && (
        <ShareMenu>
          <ShareOption onClick={handleCopyLink}>
            📋 Copy Link
          </ShareOption>
          <ShareOption onClick={handleShareOnX}>
            𝕏 Share on X
          </ShareOption>
        </ShareMenu>
      )}
      
      <ShareBtn onClick={() => setShowMenu(!showMenu)}>
        <span>↗</span>
        <span style={{ fontSize: '0.8rem' }}>Share</span>
      </ShareBtn>
      
      {copied && (
        <div style={{ 
          position: 'absolute', 
          bottom: '100%', 
          right: 0, 
          marginBottom: '0.5rem',
          background: '#5A8B5F',
          color: 'white',
          padding: '0.4rem 0.8rem',
          borderRadius: '4px',
          fontSize: '0.8rem',
          whiteSpace: 'nowrap',
          animation: 'fadeIn 0.2s ease'
        }}>
          ✓ Copied!
        </div>
      )}
    </ShareContainer>
  );
}

export default ShareButton;

